import dot from 'dot-prop-immutable';

import { PAYMENT_METHOD, SHIPPING_TYPE } from 'constants/all';

import { getErrorMessage } from 'utils/stringUtils';

import { SECTION_NAME } from './constants';
import * as types from './types';

const initialState = {
  initialFormData: {},
  isSubmitting: false,
  error: null,
  stripeError: null,
  urlParams: {
    preview: false,
    facilitate: false,
    withConfirmation: false,
  },
  canEstimateShipping: false,
  shippingTaxRate: 0,
  shipDate: null,
  quoteToken: null,
  quoteData: null,
  quoteItems: null,
  checkoutData: {
    shippingAddress: null,
    billingAddress: null,
    useShippingAddress: false,
    paymentMethod: null,
    creditCardDetails: null,
    purchaseOrderDetails: null,
    stripeToken: null,
    shippingOption: null,
    shippingRate: null,
  },
  isSectionOpenMapping: {
    [SECTION_NAME.PAYMENT_METHOD]: true,
    [SECTION_NAME.SHIPPING_INFO]: false,
    [SECTION_NAME.BILLING_INFO]: false,
    [SECTION_NAME.PAYMENT_DETAILS]: false,
  },
  addressValidation: {
    isModalOpen: false,
    isValidating: false,
    originalAddress: null,
    suggestedAddresses: [],
    error: null,
    isLastValid: false,
    isValid: false,
  },
  shippingRates: {
    isFetching: false,
    error: null,
    rates: null,
  },
};

const reducer = (state = initialState, action) => {
  const { payload } = action;
  switch (action.type) {
    case types.INIT_CHECKOUT: {
      const nextState = {
        ...initialState,
        initialFormData: payload.form,
        urlParams: payload.urlParams,
        canEstimateShipping: payload.canEstimateShipping,
        shippingTaxRate: payload.shippingTaxRate,
        shipDate: payload.shipDate,
        quoteToken: payload.token,
        quoteData: payload.quoteData,
        quoteItems: payload.quoteItems,
      };
      if (!nextState.quoteData.creditCardCheckout) {
        nextState.isSectionOpenMapping[SECTION_NAME.PAYMENT_METHOD] = false;
        nextState.isSectionOpenMapping[SECTION_NAME.SHIPPING_INFO] = true;
        nextState.checkoutData.paymentMethod = PAYMENT_METHOD.PURCHASE_ORDER;
      }
      return nextState;
    }
    case types.CHECKOUT_REQUEST:
      return { ...state, isSubmitting: true, error: null, stripeError: null };

    case types.CHECKOUT_ERROR: {
      const isStripeError = (payload || {}).code === 'stripe_error';
      const message = getErrorMessage(payload, 'Unknown error.');
      let nextState = {
        ...state,
        isSubmitting: false,
        error: isStripeError ? null : message,
        stripeError: isStripeError ? message : null,
      };
      if (isStripeError) {
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${SECTION_NAME.PAYMENT_DETAILS}`,
          true,
        );
      }
      return nextState;
    }

    case types.EDIT_SECTION: {
      let nextState = dot.set(
        state,
        `isSectionOpenMapping.${payload.section}`,
        true,
      );
      if (
        payload.section === SECTION_NAME.BILLING_INFO &&
        state.checkoutData.useShippingAddress
      ) {
        nextState = dot.set(
          nextState,
          'checkoutData.useShippingAddress',
          false,
        );
      }
      if (payload.section === SECTION_NAME.SUMMARY) {
        nextState = dot.merge(
          nextState,
          'shippingRates',
          initialState.shippingRates,
        );
      }
      return nextState;
    }

    case types.CLOSE_SECTION:
      return dot.set(state, `isSectionOpenMapping.${payload.section}`, false);

    case types.CONFIRM_CHECKOUT_DATA: {
      let nextState = dot.merge(state, 'checkoutData', payload.data);
      if (payload.section) {
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${payload.section}`,
          false,
        );
      }
      if (payload.nextSection) {
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${payload.nextSection}`,
          true,
        );
      }
      if (payload.section === SECTION_NAME.PAYMENT_METHOD) {
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${SECTION_NAME.SUMMARY}`,
          false,
        );
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${SECTION_NAME.PAYMENT_DETAILS}`,
          false,
        );
        nextState = dot.merge(nextState, 'checkoutData', {
          creditCardDetails: null,
          purchaseOrderDetails: null,
          stripeToken: null,
          shippingOption: null,
          shippingRate: null,
        });
      }
      if (payload.section === SECTION_NAME.SHIPPING_INFO) {
        nextState = dot.set(
          nextState,
          `isSectionOpenMapping.${SECTION_NAME.SUMMARY}`,
          false,
        );
        const isSupplierShippingAccountSelected =
          state.checkoutData.shippingOption &&
          state.checkoutData.shippingOption.type ===
            SHIPPING_TYPE.SUPPLIER_ACCOUNT;
        if (isSupplierShippingAccountSelected) {
          nextState = dot.merge(nextState, 'checkoutData', {
            shippingOption: null,
            shippingRate: null,
          });
        }
      }
      if (payload.section === SECTION_NAME.PAYMENT_DETAILS) {
        nextState = { ...nextState, stripeError: null };
      }
      return { ...nextState, error: null };
    }

    case types.VALIDATE_ADDRESS_REQUEST:
      return dot.merge(state, 'addressValidation', {
        isValidating: true,
        error: null,
      });

    case types.VALIDATE_ADDRESS_SUCCESS:
      return dot.merge(state, 'addressValidation', {
        isValidating: false,
        isLastValid: payload.isValid,
      });

    case types.VALIDATE_ADDRESS_PICK_FROM_SUGGESTED:
      return dot.merge(state, 'addressValidation', {
        isModalOpen: true,
        originalAddress: payload.originalAddress,
        suggestedAddresses: payload.suggestedAddresses,
      });

    case types.VALIDATE_ADDRESS_ERROR:
      return dot.merge(state, 'addressValidation', {
        isValidating: false,
        error: getErrorMessage(payload, 'Unknown error.'),
      });

    case types.VALIDATE_ADDRESS_CONFIRM:
      return dot.merge(state, 'addressValidation', {
        isModalOpen: false,
        isValid: state.addressValidation.isLastValid,
      });

    case types.VALIDATE_ADDRESS_CANCEL:
    case types.VALIDATE_ADDRESS_EDIT:
      return dot.merge(state, 'addressValidation', {
        isModalOpen: false,
      });

    case types.FETCH_SHIPPING_RATES_REQUEST:
      return dot.merge(state, 'shippingRates', {
        isFetching: true,
        error: null,
        rates: null,
      });

    case types.FETCH_SHIPPING_RATES_SUCCESS:
      return dot.merge(state, 'shippingRates', {
        isFetching: false,
        rates: payload.shippingRates,
        shipDate: payload.shipDate,
      });

    case types.FETCH_SHIPPING_RATES_ERROR:
      return dot.merge(state, 'shippingRates', {
        isFetching: false,
        error:
          payload.message ||
          "Unfortunately, we can't estimate shipping prices for your package.",
      });

    default:
      return state;
  }
};

export default reducer;
